import React from 'react';
import { Editor, type OnMount } from '@monaco-editor/react';
import type { editor as monacoEditor } from 'monaco-editor';
import {
  Maximize2,
  Minimize2,
  FileText,
  Copy,
  Check,
  Eye,
  EyeOff,
  List,
  ListOrdered,
  TextQuote,
  Link,
  Minus,
} from 'lucide-react';
import styles from './MarkdownEditor.module.css';
import { validateAndCleanInput } from '../../utils/security';
import { renderMarkdownToHtml } from '../../shared/widget/markdown';
import { toast } from '../../lib/toast';
import {
  toggleBoldSelection,
  toggleItalicSelection,
  toggleInlineCodeSelection,
  toggleHeading,
  prefixLines,
  insertCodeBlock,
  insertAtCursor,
  toggleLinkSelection,
} from './markdownEditorHelpers';

const MAX_MARKDOWN_LENGTH = 50000;

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  height?: string;
  readOnly?: boolean;
  title?: string;
  filename?: string;
  showPreview?: boolean;
}

interface FormatButtonProps {
  label: React.ReactNode;
  title: string;
  disabled?: boolean;
  onAction: () => void;
}

function FormatButton({ label, title, disabled, onAction }: FormatButtonProps) {
  return (
    <button
      type="button"
      className={styles.formatBtn}
      title={title}
      disabled={disabled}
      onMouseDown={e => {
        e.preventDefault();
        onAction();
      }}
    >
      {label}
    </button>
  );
}

export default function MarkdownEditor({
  value,
  onChange,
  height = '420px',
  readOnly = false,
  title = 'System Prompt',
  filename = 'system-prompt.md',
  showPreview: initialShowPreview = true,
}: MarkdownEditorProps) {
  const editorRef = React.useRef<monacoEditor.IStandaloneCodeEditor | null>(null);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [tempValue, setTempValue] = React.useState(value);
  const [copied, setCopied] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(initialShowPreview);

  React.useEffect(() => {
    setTempValue(value);
  }, [value]);

  const activeValue = isExpanded ? tempValue : value;

  const setCurrentValue = (val: string) => {
    if (isExpanded) {
      setTempValue(val);
    } else {
      onChange(val);
    }
  };

  const handleMount: OnMount = (editor) => {
    editorRef.current = editor;
  };

  const applyFormat = (action: () => void) => {
    if (readOnly) return;
    action();
    const editor = editorRef.current;
    if (editor) {
      setCurrentValue(editor.getValue());
    }
  };

  const handleEditorChange = (newValue: string | undefined) => {
    const val = newValue ?? '';
    const sanitized = validateAndCleanInput(val, false);
    if (sanitized.length > MAX_MARKDOWN_LENGTH) return;
    setCurrentValue(sanitized);
  };

  const copyToClipboard = async () => {
    if (!activeValue.trim()) return;
    try {
      await navigator.clipboard.writeText(activeValue);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleExpand = () => {
    setTempValue(value);
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    onChange(tempValue);
    setIsExpanded(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsExpanded(false);
  };

  const editorOptions = {
    readOnly,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 2,
    wordWrap: 'on' as const,
    lineNumbers: 'on' as const,
    folding: true,
    autoIndent: 'full' as const,
    formatOnPaste: true,
    overviewRulerLanes: 0,
    renderValidationDecorations: 'off' as const,
    padding: { top: 12, bottom: 12 },
    fontSize: 13,
  };

  const renderFormatToolbar = () => (
    <div className={styles.formatToolbar}>
      <span className={styles.formatToolbarLabel}>Format</span>

      <div className={styles.formatGroup}>
        <FormatButton
          label={<strong className={styles.formatBtnBold}>B</strong>}
          title="Bold"
          disabled={readOnly}
          onAction={() => applyFormat(() => {
            const editor = editorRef.current;
            if (editor) toggleBoldSelection(editor);
          })}
        />
        <FormatButton
          label={<em className={styles.formatBtnItalic}>I</em>}
          title="Italic"
          disabled={readOnly}
          onAction={() => applyFormat(() => {
            const editor = editorRef.current;
            if (editor) toggleItalicSelection(editor);
          })}
        />
        <FormatButton
          label={<span className={styles.formatBtnMono}>{'<>'}</span>}
          title="Inline code"
          disabled={readOnly}
          onAction={() => applyFormat(() => {
            const editor = editorRef.current;
            if (editor) toggleInlineCodeSelection(editor);
          })}
        />
      </div>

      <span className={styles.formatDivider} />

      <div className={styles.formatGroup}>
        <FormatButton label="H1" title="Heading 1" disabled={readOnly} onAction={() => applyFormat(() => {
          const editor = editorRef.current;
          if (editor) toggleHeading(editor, 1);
        })} />
        <FormatButton label="H2" title="Heading 2" disabled={readOnly} onAction={() => applyFormat(() => {
          const editor = editorRef.current;
          if (editor) toggleHeading(editor, 2);
        })} />
        <FormatButton label="H3" title="Heading 3" disabled={readOnly} onAction={() => applyFormat(() => {
          const editor = editorRef.current;
          if (editor) toggleHeading(editor, 3);
        })} />
      </div>

      <span className={styles.formatDivider} />

      <div className={styles.formatGroup}>
        <FormatButton
          label={<List size={15} strokeWidth={2.25} />}
          title="Bullet list"
          disabled={readOnly}
          onAction={() => applyFormat(() => {
            const editor = editorRef.current;
            if (editor) prefixLines(editor, '- ');
          })}
        />
        <FormatButton
          label={<ListOrdered size={15} strokeWidth={2.25} />}
          title="Numbered list"
          disabled={readOnly}
          onAction={() => applyFormat(() => {
            const editor = editorRef.current;
            if (editor) prefixLines(editor, '1. ');
          })}
        />
        <FormatButton
          label={<TextQuote size={15} strokeWidth={2.25} />}
          title="Blockquote"
          disabled={readOnly}
          onAction={() => applyFormat(() => {
            const editor = editorRef.current;
            if (editor) prefixLines(editor, '> ');
          })}
        />
      </div>

      <span className={styles.formatDivider} />

      <div className={styles.formatGroup}>
        <FormatButton
          label={<Link size={15} strokeWidth={2.25} />}
          title="Link"
          disabled={readOnly}
          onAction={() => applyFormat(() => {
            const editor = editorRef.current;
            if (editor) toggleLinkSelection(editor);
          })}
        />
        <FormatButton
          label={<span className={styles.formatBtnMono}>{'{ }'}</span>}
          title="Code block"
          disabled={readOnly}
          onAction={() => applyFormat(() => {
            const editor = editorRef.current;
            if (editor) insertCodeBlock(editor);
          })}
        />
        <FormatButton
          label={<Minus size={15} strokeWidth={2.25} />}
          title="Horizontal rule"
          disabled={readOnly}
          onAction={() => applyFormat(() => {
            const editor = editorRef.current;
            if (editor) insertAtCursor(editor, '\n---\n');
          })}
        />
      </div>
    </div>
  );

  const renderSplitPanel = (editorHeight: string, editorValue: string) => (
    <div className={styles.splitLayout}>
      <div className={`${styles.editorPane} ${showPreview ? styles.editorPaneSplit : ''}`}>
        <Editor
          height={editorHeight}
          language="markdown"
          value={editorValue}
          onChange={handleEditorChange}
          onMount={handleMount}
          options={editorOptions}
          theme="vs"
        />
      </div>
      {showPreview && (
        <div className={styles.previewPane} style={{ height: editorHeight }}>
          <span className={styles.previewLabel}>Preview</span>
          {editorValue.trim() ? (
            <div
              className={styles.previewContent}
              dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(editorValue) }}
            />
          ) : (
            <p className={styles.previewEmpty}>Start writing to see a preview.</p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <FileText size={16} className={styles.headerIcon} />
            <span className={styles.filename}>{filename}</span>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              onClick={() => setShowPreview(prev => !prev)}
              className={styles.headerBtn}
              title={showPreview ? 'Hide preview' : 'Show preview'}
            >
              {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
              {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={copyToClipboard}
              className={`${styles.headerBtn} ${copied ? styles.headerBtnSuccess : ''}`}
              title="Copy to clipboard"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button type="button" onClick={handleExpand} className={styles.headerBtn} title="Expand editor">
              <Maximize2 size={14} />
              Expand
            </button>
          </div>
        </div>

        {!readOnly && !isExpanded && renderFormatToolbar()}

        <div className={styles.editorWrapper}>
          {renderSplitPanel(height, isExpanded ? tempValue : value)}
        </div>
      </div>

      {isExpanded && (
        <div className={styles.modal}>
          <div className={styles.modalOverlay} onClick={handleCancel} />
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>
                <FileText size={18} />
                {title}
              </h3>
              <div className={styles.modalActions}>
                <button type="button" onClick={() => setShowPreview(prev => !prev)} className={styles.headerBtn}>
                  {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
                  {showPreview ? 'Hide Preview' : 'Preview'}
                </button>
                <button type="button" onClick={handleCollapse} className={styles.saveBtn}>
                  Save & Close
                </button>
                <button type="button" onClick={handleCancel} className={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="button" onClick={handleCollapse} className={styles.headerBtn} title="Collapse editor">
                  <Minimize2 size={14} />
                  Collapse
                </button>
              </div>
            </div>
            {!readOnly && renderFormatToolbar()}
            <div className={styles.modalEditor}>
              {renderSplitPanel('calc(100vh - 200px)', tempValue)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
